import {Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ApiService} from "../api.service";
import levenshtein from 'fast-levenshtein';
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatButton, MatFabButton} from "@angular/material/button";
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {FormsModule} from "@angular/forms";
import {AudioService} from "../audio.service";

@Component({
  selector: 'app-mic-check',
  imports: [
    MatIcon,
    MatProgressSpinner,
    MatButton,
    MatButtonToggleGroup,
    MatButtonToggle,
    FormsModule,
    MatFabButton
  ],
  templateUrl: './mic-check.component.html',
  styleUrl: './mic-check.component.css'
})
export class MicCheckComponent implements OnInit, OnDestroy {

  private pc: any;
  public respText: string = '';
  private key: string = '';
  private checkText = '';
  private threshold: string = "0";
  public loading: boolean = false;
  public micReady: boolean = false;
  public checkComplete: boolean = false;
  public checkFailed: boolean = false;
  public instructions: string = '';
  public checkInProgress: boolean = false;
  public gender: string = "1";
  private audioSource: any;
  public agentSpeaking: boolean = false;
  public thinking: boolean = false;
  public agentText: string = '';
  private stream: any;
  public silence_duration: number = 0;
  @ViewChild('waveformCanvas') waveformCanvas!: ElementRef<HTMLCanvasElement>;

  @Input()
  aid: string = '';
  @Input()
  complete: boolean = false;

  constructor(private api: ApiService, private router: Router, private snack: MatSnackBar, private ngZone: NgZone,
              private audioService: AudioService,) {
  }

  async ngOnInit() {
    this.loading = true;
    this.key = await this.api.getOpenAiToken();
    if (this.api.userData.sex_pronoun) this.gender = ""+this.api.userData.sex_pronoun;
    else this.gender = ""+this.api.userData.sex;
    this.checkText = this.api.micCheckText;
    this.threshold = this.api.micCheckThreshold;
    this.silence_duration = Number(this.api.silenceDuration) / 1000;
    if (await this.checkMic()) {
      this.micReady = true;
    }
    this.loading = false;
  }

  ngOnDestroy() {
    try {
      this.pc?.close();
      this.audioSource?.stop();
      this.stream?.getTracks().forEach((track: any) => track.stop());
    }
    catch (e) {
      console.log(e);
    }
  }

  private async checkMic() {
    try {
      const mic = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      mic.getTracks().forEach((track: any) => track.stop());
      return true;
    }
    catch (e) {
      console.log(e);
      return false;
    }
  }

  public async startCheck() {
    try {
      await this.audioService.unlock();

      this.checkInProgress = true;
      this.respText = '';
      this.instructions = 'Poslouchejte';
      this.agentSpeaking = true;

      const blob = await this.fetchTtsBlob(this.checkText);

      await this.audioService.playBlob(blob, (audioEl) => {
        // Waveform setup starts here
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(audioEl);
        const analyser = audioCtx.createAnalyser();
        source.connect(analyser);
        analyser.connect(audioCtx.destination);

        this.drawWaveform(analyser, this.waveformCanvas.nativeElement);

        if (audioCtx.state === 'suspended') {
          audioCtx.resume();
        }
      });

      this.ngZone.run(() => {
        this.agentText = this.checkText;
      });
      await this.transcribeVoice();

    } catch (err) {
      console.error('Playback failed', err);
    }
  }

  private async fetchTtsBlob(text: string): Promise<Blob> {
    const response = await fetch(`${this.api.apiUrl}/auth/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': this.api.authToken
      },
      body: JSON.stringify({ text }),
    });

    const arrayBuffer = await response.arrayBuffer();
    return new Blob([arrayBuffer], { type: 'audio/mpeg' });
  }

  private evaluate() {
    this.ngZone.run(() => {
      this.checkInProgress = false;
    });
    this.thinking = true;
    const distance = levenshtein.get(this.checkText, this.respText);
    if (distance > Number(this.threshold)) {
      this.checkFailed = true;
    }
    else {
      this.checkComplete = true;
      this.finish();
    }
    this.thinking = false;
  }

  private async transcribeVoice() {
    console.log('Transcribing');
    this.pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
      ]
    });

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: true
    });
    this.pc.addTrack(this.stream.getTracks()[0]);

    const dc = this.pc.createDataChannel("oai-events");
    dc.addEventListener("message", (e: any) => {
      // Realtime server events appear here!
      const d = JSON.parse(e.data);
      if (d.delta) this.respText += d.delta;
      if (d.type == "conversation.item.input_audio_transcription.completed") {
        this.respText = d.transcript;
        this.pc.close();
        this.evaluate();
      }
      if (d.type == "transcription_session.created") {
        this.instructions = 'Zopakujte, co jste slyšeli';
        this.agentSpeaking = false;
      }
      console.log(d);
    });

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const sdpResponse = await fetch(`${this.api.openAiUrl}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${this.key}`,
        "Content-Type": "application/sdp"
      }
    });

    const answer: RTCSessionDescriptionInit = {
      type: "answer",
      sdp: await sdpResponse.text(),
    };
    await this.pc.setRemoteDescription(answer);
  }

  goToDashboard() {
    this.router.navigate(['dashboard']);
  }

  private async finish() {
    this.pc?.close();
    if (!this.complete) await this.api.setActivityState(this.aid, 'complete');
    this.snack.open('Aktivita úspěšně dokončena', 'Zavřít', {duration: 3000});
    // setTimeout(() => {
    //   //this.router.navigate(['dashboard']);
    // }, 5000);
  }

  public async changeGender() {
    let g: number = 1;
    if (this.gender == "2") g = 2;
    this.api.changeGender(g);
  }

  private drawWaveform(analyser: AnalyserNode, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const width = canvas.width;
    const height = canvas.height;

    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0.0, "#00c3ff");
    gradient.addColorStop(0.25, "#ff5fc0");
    gradient.addColorStop(0.5, "#ff9a44");
    gradient.addColorStop(0.75, "#f06292");
    gradient.addColorStop(1.0, "#ff6f00");

    const draw = () => {
      requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = gradient;
      ctx.beginPath();

      const sliceWidth = width / dataArray.length;
      let x = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * height / 2;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.stroke();

      ctx.beginPath();
      x = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = dataArray[i] / 128.0;
        const y = height - (v * height / 2);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.stroke();
    };

    draw();
  }

}
